/**
 * usePermissions Hook
 * Provides granular permission checking for modules and sub-pages
 * 
 * Usage:
 *   const permissions = usePermissions(user, 'hiring');
 *   permissions.canView        // Check if user can view the module
 *   permissions.canCreate      // Check if user can create in the module
 *   permissions.canEdit        // Check if user can edit in the module
 *   permissions.canDelete      // Check if user can delete in the module
 *   permissions.canViewSubPage('documents')     // Check sub-page view permission
 *   permissions.canCreateSubPage('documents')   // Check sub-page create permission
 *   permissions.canEditSubPage('documents')     // Check sub-page edit permission
 *   permissions.canDeleteSubPage('documents')   // Check sub-page delete permission
 *   permissions.canUploadSubPage('uploadedDocuments')   // Check sub-page upload permission
 *   permissions.canDownloadSubPage('uploadedDocuments') // Check sub-page download permission
 *   permissions.canReplaceSubPage('uploadedDocuments')  // Check sub-page replace permission
 */

import { useMemo } from 'react';

// Visible version stamp — paste `window.__USE_PERMISSIONS_VERSION` in
// DevTools console to confirm which build is running. If this is missing
// or shows an older value, the browser is serving a cached JS bundle.
if (typeof window !== 'undefined') {
  window.__USE_PERMISSIONS_VERSION = '2026-02-26-truthy-fix-v3-both-hooks';
}

export const usePermissions = (user, module) => {
  // TOP-LEVEL diagnostic for hiring — fires on EVERY render, not memoized.
  // If this log appears but `[usePermissions:hiring]` does not, the issue
  // is that useMemo is returning a cached value without re-running.
  if (typeof window !== 'undefined' && module === 'hiring') {
    // eslint-disable-next-line no-console
    console.log('[usePermissions:call]', {
      hasUser: !!user,
      email: user?.email,
      role: user?.role,
      hasPermissions: !!user?.permissions,
      hiringPermsKeys: user?.permissions?.hiring ? Object.keys(user.permissions.hiring) : null,
      rawViewOwnOnly: user?.permissions?.hiring?.viewOwnOnly,
      rawViewOwnOnlyType: typeof user?.permissions?.hiring?.viewOwnOnly,
    });
    // Also expose the live user object for inspection
    window.__LAST_HIRING_USER = user;
  }

  return useMemo(() => {
    // Super Admin and Admin have full access
    const isAdmin = user?.role === 'Super Admin' || user?.role === 'Admin';
    
    // Get module permissions from user object
    const modulePermissions = user?.permissions?.[module] || {};
    
    // Default permissions if none set - admins get full access, others get view only
    const defaultPerms = {
      view: isAdmin,
      create: isAdmin,
      edit: isAdmin,
      delete: isAdmin,
      subPages: {}
    };
    
    // Merge defaults with user permissions
    const perms = {
      ...defaultPerms,
      ...modulePermissions,
      subPages: {
        ...defaultPerms.subPages,
        ...(modulePermissions.subPages || {})
      }
    };
    
    // Truthy check that tolerates legacy/string values ("true", 1, true)
    // Some older saves may have persisted these as strings; treat them
    // consistently as booleans here.
    const isTruthy = (v) =>
      v === true || v === 'true' || v === 1 || v === '1';

    const rawViewOwnOnly = isTruthy(perms.viewOwnOnly);

    // Diagnostic log on hiring module — helps debug production reports of
    // "Hiring Agent sees all candidates". Logs regardless of value so we
    // can see exactly what the hook is computing for a given user.
    if (typeof window !== 'undefined' && module === 'hiring' && user?.email) {
      // eslint-disable-next-line no-console
      console.log('[usePermissions:hiring]', {
        email: user?.email,
        role: user?.role,
        isAdmin,
        rawViewOwnOnly,
        viewOwnOnlyRawValue: perms.viewOwnOnly,
        modulePermissionsKeys: Object.keys(modulePermissions),
        effectiveViewOwnOnly: !isAdmin && rawViewOwnOnly,
      });
    }

    return {
      // Main module permissions - admins always have access
      canView: isAdmin || perms.view === true,
      canCreate: isAdmin || perms.create === true,
      canEdit: isAdmin || perms.edit === true,
      canDelete: isAdmin || perms.delete === true,
      
      // Special permissions
      // Admins always see all — viewOwnOnly only applies to non-admin roles
      viewOwnOnly: !isAdmin && rawViewOwnOnly,
      
      // Sub-page permission checkers
      canViewSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.view === true;
      },
      
      canCreateSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.create === true;
      },
      
      canEditSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.edit === true;
      },
      
      canDeleteSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.delete === true;
      },
      
      // Document-specific permission checkers
      canUploadSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.upload === true;
      },
      
      canDownloadSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.download === true;
      },
      
      canReplaceSubPage: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        return subPagePerms?.replace === true;
      },
      
      // Helper to check if user has any permission for a sub-page
      hasSubPageAccess: (subPage) => {
        if (isAdmin) return true;
        const subPagePerms = perms.subPages?.[subPage];
        if (!subPagePerms) return false;
        return subPagePerms.view || subPagePerms.create || subPagePerms.edit || subPagePerms.delete || 
               subPagePerms.upload || subPagePerms.download || subPagePerms.replace;
      },
      
      // Raw permissions object for advanced use
      raw: perms,
      
      // User info
      isAdmin
    };
  }, [user, module]);
};

export default usePermissions;
